'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { createPortal } from 'react-dom'
import { toast } from 'react-toastify'
import { X, CheckCircle, Clock, ThumbsUp, ThumbsDown, ExternalLink, Infinity, AlertTriangle, Copy, Check, FileText, ChevronDown } from 'lucide-react'
import { apiUpdateOfferLikes } from '@/apis/offers'
import { Offer } from '@/services/dataTypes'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag } from '@/constants/hooks'
import { copyToClipboard } from '../utils/clipboard'

interface Props {
    data: Offer
    companyId: string
    onClose: () => void
    domain: string
    merchantHref?: string
    merchantImg?: string | null
    productImg?: string | null
    finalDiscountTag?: string | null
}

const OfferModal = ({ data, companyId, onClose, domain, merchantHref, merchantImg, productImg, finalDiscountTag }: Props) => {
    const [mounted, setMounted] = useState(false)
    const [hasLiked, setHasLiked] = useState(false)
    const [loading, setLoading] = useState(false)
    const [copied, setCopied] = useState(false)
    const [revealed, setRevealed] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        setMounted(true)
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = '' }
    }, [])

    useEffect(() => {
        if (data?.unique_id && typeof window !== 'undefined') {
            const lastLiked = localStorage.getItem(`hasLiked_${data.unique_id}`)
            if (lastLiked && Date.now() - parseInt(lastLiked) < 86400000) setHasLiked(true)
        }
    }, [data?.unique_id])

    const handleLike = async (val: number) => {
        if (hasLiked) { toast.error('Already rated, try again later!', { autoClose: 2000 }); return }
        try {
            const res = await apiUpdateOfferLikes(companyId, data?.unique_id, val)
            if (res.status === 'success') {
                toast.success('Thank you for your feedback!', { autoClose: 2000 })
                localStorage.setItem(`hasLiked_${data.unique_id}`, Date.now().toString())
                setHasLiked(true)
            }
        } catch { toast.error('Something went wrong.', { autoClose: 2000 }) }
    }

    const handleGetDeal = () => {
        setLoading(true)
        const finalUrl = data?.url?.startsWith('/') ? data.url.replace(/^\/+/, '') : (data?.url ?? '')
        setTimeout(() => { window.open(`/${finalUrl}`, '_blank', 'noopener,noreferrer'); setLoading(false) }, 800)
    }

    const handleCopyCode = async () => {
        if (!data?.coupon_code) return
        try {
            await navigator.clipboard.writeText(data.coupon_code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            try {
                const el = document.createElement('textarea')
                el.value = data.coupon_code
                el.style.position = 'fixed'
                el.style.opacity = '0'
                const container = document.body || document.documentElement
                if (container) {
                    container.appendChild(el)
                    el.focus()
                    el.select()
                    document.execCommand('copy')
                    container.removeChild(el)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                }
            } catch {
                toast.error('Failed to copy. Please copy manually.')
            }
        }
    }

    const duration = calculateOfferDuration(data?.end_date ?? null)
    const originalPrice = data?.original_price ? parseFloat(data.original_price) : 0
    const salePrice = data?.sale_price ? parseFloat(data.sale_price) : 0
    const discountPct = originalPrice > 0 && salePrice > 0 ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const tag = finalDiscountTag ?? getFinalDiscountTag(data?.offer_title || data?.offer_detail, discountPct)
    const imgSrc = productImg ? getBaseImageUrl(domain, productImg, '') : merchantImg ? getBaseImageUrl(domain, merchantImg, '') : null
    const currency = getCurrencySymbol(data?.currency)
    const isCoupon = !!data?.coupon_code

    if (!mounted || !data) return null

    return createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ willChange: 'transform', isolation: 'isolate', transform: 'translateZ(0)' }}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Modal */}
            <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-3xl overflow-hidden"
                style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.15)', boxShadow: '0 24px 60px rgba(45,62,45,0.18)' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
                    style={{ borderBottom: '1px solid rgba(92,122,92,0.1)' }}>
                    <div className="flex items-center gap-2">
                        <CheckCircle size={16} style={{ color: '#5C7A5C' }} />
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#5C7A5C' }}>Verified Offer</span>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
                        style={{ background: 'rgba(92,122,92,0.08)', color: '#2D3E2D' }}>
                        <X size={15} />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

                    {/* Image + title */}
                    <div className="flex items-start gap-4">
                        {imgSrc && (
                            <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 p-2"
                                style={{ background: '#E8F0E8', border: '1px solid rgba(92,122,92,0.15)' }}>
                                <Image src={imgSrc} alt={data?.offer_title ?? ''} width={64} height={64} className="object-contain" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-black leading-snug mb-2" style={{ color: '#2D3E2D', fontFamily: 'Georgia, serif' }}>
                                {discardHTMLTags(data?.offer_title)}
                            </h2>
                            {/* Badges */}
                            <div className="flex flex-wrap gap-1.5">
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full"
                                    style={{ background: '#E8F0E8', color: '#5C7A5C', border: '1px solid rgba(92,122,92,0.2)' }}>
                                    {duration === 'Life Time' ? <Infinity size={9} /> : duration === 'Offer expired' ? <AlertTriangle size={9} /> : <Clock size={9} />}
                                    {duration}
                                </span>
                                {tag && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: '#5C7A5C' }}>
                                        {tag}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pricing */}
                    {salePrice > 0 && (
                        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                            style={{ background: '#E8F0E8', border: '1px solid rgba(92,122,92,0.15)' }}>
                            <span className="text-xl font-bold" style={{ color: '#2D3E2D' }}>{currency}{salePrice.toFixed(2)}</span>
                            {originalPrice > 0 && <span className="text-sm line-through" style={{ color: 'rgba(45,62,45,0.4)' }}>{currency}{originalPrice.toFixed(2)}</span>}
                            {discountPct && <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white ml-auto" style={{ background: '#5C7A5C' }}>{discountPct}%</span>}
                        </div>
                    )}

                    {/* Coupon code matching Theme 5 */}
                    {isCoupon && (
                        <div className="bg-gradient-to-br from-[#E8F0E8]/50 via-[#E8F0E8]/20 to-transparent border border-dashed border-[#5C7A5C]/40 p-4 sm:p-5 rounded-2xl sm:rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                            <div className="text-center sm:text-left">
                                <span className="text-[9px] font-black text-[#5C7A5C] uppercase tracking-widest flex items-center gap-1 justify-center sm:justify-start">
                                    Coupon Code
                                </span>
                                <p className="text-xl sm:text-2xl font-mono font-black text-[#2D3E2D] tracking-wider mt-0.5 sm:mt-1 select-all">
                                    {data.coupon_code}
                                </p>
                            </div>
                            <button
                                onClick={handleCopyCode}
                                className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-[#2D3E2D] hover:bg-[#1E2B1E] text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 shadow-md active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                            >
                                {copied ? <Check size={13} /> : <Copy size={13} />}
                                <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                            </button>
                        </div>
                    )}

                    {/* Description */}
                    {data?.offer_detail && (() => {
                        const cleanDetail = discardHTMLTags(data.offer_detail)
                        const isLong = cleanDetail.length > 120
                        return (
                            <div className="pt-2 border-t border-slate-100">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider mb-1 text-[#2D3E2D] flex items-center gap-1.5">
                                    <FileText size={12} className="text-[#5C7A5C]" />
                                    Description
                                </h4>
                                <p className={`text-xs sm:text-sm leading-relaxed text-slate-600 ${isLong && !isExpanded ? 'line-clamp-3' : ''}`}>
                                    {cleanDetail}
                                </p>
                                {isLong && (
                                    <button
                                        type="button"
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-[#5C7A5C] hover:underline cursor-pointer"
                                    >
                                        <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                                        <ChevronDown size={13} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            </div>
                        )
                    })()}

                    {/* CTA */}
                    <button onClick={handleGetDeal} disabled={loading}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-[#2D3E2D] shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-70 cursor-pointer"
                        style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}>
                        {loading ? (
                            <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg> Redirecting...</>
                        ) : (
                            <><ExternalLink size={14} /> {isCoupon ? 'Shop Now' : 'Get Deal'}</>
                        )}
                    </button>

                    {merchantHref && (
                        <Link href={merchantHref} onClick={onClose}
                            className="flex items-center justify-center text-xs font-semibold transition-colors hover:opacity-70"
                            style={{ color: '#5C7A5C' }}>
                            View all offers from this store
                        </Link>
                    )}
                </div>

                {/* Footer — feedback */}
                <div className="flex items-center justify-between gap-4 px-6 py-4 flex-shrink-0 flex-wrap"
                    style={{ borderTop: '1px solid rgba(92,122,92,0.1)', background: '#FAF7F2' }}>
                    <span className="text-xs font-medium" style={{ color: 'rgba(45,62,45,0.5)' }}>Did this work for you?</span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => handleLike(1)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all hover:-translate-y-0.5"
                            style={{ background: '#5C7A5C' }}>
                            <ThumbsUp size={11} /> Yes
                        </button>
                        <button onClick={() => handleLike(0)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all hover:-translate-y-0.5"
                            style={{ background: 'rgba(92,122,92,0.1)', color: '#2D3E2D' }}>
                            <ThumbsDown size={11} /> No
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default OfferModal
